import { sha256 } from "@-/com.npmjs.noble__hashes/sha2";
import { getBundledActorMigrationTags } from "#actor-migration-tags";

/**
 * How the app and the sync gateway tell each other which migrations a copy of the actor database
 * has (see `database.md`, "Server: sync gateway").
 *
 * The journal (`__drizzle_migrations`) is compared by migration NAME. The history is linear and
 * immutable (the migrations lock enforces it), so a list of names identifies one schema. Names are
 * also all a device has for a migration its own bundle does not carry, which happens whenever the
 * cloud copy is newer than the app.
 *
 * A head is "how many migrations, and a hash of their names in order". It only ever moves forward.
 */
export interface ActorJournalHead {
  count: number;
  hash: string;
}

/**
 * The wire protocol between the app and the gateway. A request below the gateway's minimum is
 * answered with "update required". Bump it only for a change an older app cannot speak; a new
 * migration never needs it.
 */
export const ACTOR_SYNC_PROTOCOL = 2;
export const ACTOR_SYNC_PROTOCOL_HEADER = "x-haniverse-sync-protocol";
/** Request: the head of the replica's journal, as `<count>.<hash>`. */
export const ACTOR_SYNC_HEAD_HEADER = "x-haniverse-sync-head";
/** Response: how many statements of this upload were set aside instead of applied. */
export const ACTOR_SYNC_SET_ASIDE_HEADER = "x-haniverse-sync-set-aside";
/** Response: why the gateway did not serve the request, as an `ActorSyncGatewayRefusal`. */
export const ACTOR_SYNC_REFUSAL_HEADER = "x-haniverse-sync-refusal";
/**
 * Request: `<index>/<count>`, on an upload the device sent in parts because it was too large for
 * one request (see `actor-sync-fetch.ts`). Every part is a complete upload in the engine's own
 * shape; only the last one advances the engine's cursor.
 */
export const ACTOR_SYNC_PART_HEADER = "x-haniverse-sync-part";

/** Every reason the gateway answers a sync request itself instead of serving it. */
export type ActorSyncGatewayRefusal =
  /** The device credential is missing, malformed or revoked. Ask `ensure` for a new one. */
  | "credential"
  /** The account's cloud database is no longer the one this replica was made from. */
  | "database-moved"
  /** The replica has migrations this server does not know yet. Retry; the server is deploying. */
  | "server-behind"
  /** The cloud copy or the server could not be reached or prepared. Retry. */
  | "unavailable"
  /** The app speaks a protocol this server no longer accepts. Update the app. */
  | "update-required";

const HEAD_HASH_LENGTH = 16;
const HEAD_FORMAT = /^(?<count>\d{1,6})\.(?<hash>[0-9a-f]{16})$/;

/** The head of a journal that holds exactly these migrations, in order. */
export function createActorJournalHead(names: readonly string[]): ActorJournalHead {
  const digest = sha256(new TextEncoder().encode(JSON.stringify(names)));
  return {
    count: names.length,
    hash: Array.from(digest, (byte) => byte.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, HEAD_HASH_LENGTH),
  };
}

export function formatActorJournalHead(head: ActorJournalHead): string {
  return `${head.count}.${head.hash}`;
}

export function parseActorJournalHead(value: string | null | undefined): ActorJournalHead | null {
  if (value === null || value === undefined) {
    return null;
  }
  const match = HEAD_FORMAT.exec(value);
  const count = match?.groups?.["count"];
  const hash = match?.groups?.["hash"];
  return count === undefined || hash === undefined ? null : { count: Number(count), hash };
}

/**
 * Whether `head` is a point in this bundle's own history: the first `head.count` of its
 * migrations. A head with more migrations than the bundle, or with other ones, is not known.
 */
export function isKnownActorJournalHead(
  head: ActorJournalHead,
  bundle: readonly string[] = getBundledActorMigrationTags(),
): boolean {
  return (
    head.count <= bundle.length &&
    createActorJournalHead(bundle.slice(0, head.count)).hash === head.hash
  );
}

/**
 * How a journal relates to this bundle's history:
 * - `behind`: it holds the first migrations of the bundle and lacks later ones;
 * - `current`: it holds exactly the bundle's migrations;
 * - `ahead`: it holds every migration of the bundle and more (a newer server or app wrote it);
 * - `diverged`: it holds a migration the bundle does not have at that position. A linear,
 *   immutable history never produces this; it means the copy was written by something else.
 */
export function compareActorJournalToBundle(
  names: readonly string[],
  bundle: readonly string[] = getBundledActorMigrationTags(),
): "ahead" | "behind" | "current" | "diverged" {
  const shared = Math.min(names.length, bundle.length);
  if (names.slice(0, shared).some((name, index) => name !== bundle[index])) {
    return "diverged";
  }
  if (names.length === bundle.length) {
    return "current";
  }
  return names.length < bundle.length ? "behind" : "ahead";
}
